import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { Paprika } from '../../styles/theme';

export const PEOPLE_MIN = 1;
export const PEOPLE_MAX = 12;

interface Props {
  value: number;
  onChange: (next: number) => void;
  /** « {{count}} personnes » next to the number. */
  unit: string;
  fewerLabel: string;
  moreLabel: string;
  testID: string;
}

/**
 * « Tu cuisines pour combien de personnes ? » — a −/+ stepper clamped to the
 * same 1..12 range as Settings' default servings, with a display digit.
 */
export function PeopleStepper({ value, onChange, unit, fewerLabel, moreLabel, testID }: Props) {
  const atMin = value <= PEOPLE_MIN;
  const atMax = value >= PEOPLE_MAX;
  return (
    <View style={styles.row} testID={testID}>
      <Pressable
        onPress={() => onChange(Math.max(PEOPLE_MIN, value - 1))}
        disabled={atMin}
        testID={`${testID}-minus`}
        accessibilityRole="button"
        accessibilityLabel={fewerLabel}
        accessibilityState={{ disabled: atMin }}
        style={[styles.circle, atMin && styles.circleDisabled]}
      >
        <Feather name="minus" size={20} color={Paprika.accent} />
      </Pressable>
      <Text style={styles.number} testID={`${testID}-value`}>{value}</Text>
      <Pressable
        onPress={() => onChange(Math.min(PEOPLE_MAX, value + 1))}
        disabled={atMax}
        testID={`${testID}-plus`}
        accessibilityRole="button"
        accessibilityLabel={moreLabel}
        accessibilityState={{ disabled: atMax }}
        style={[styles.circle, atMax && styles.circleDisabled]}
      >
        <Feather name="plus" size={20} color={Paprika.accent} />
      </Pressable>
      <Text style={styles.unit}>{unit}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', gap: 16 },
  circle: {
    width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center',
    backgroundColor: Paprika.cardBg, borderWidth: 1, borderColor: Paprika.hairline,
    boxShadow: '0 6px 18px -10px rgba(82, 68, 60, 0.35)',
  },
  circleDisabled: { opacity: 0.35 },
  number: { fontFamily: 'Newsreader-Regular', fontSize: 44, lineHeight: 48, minWidth: 56, textAlign: 'center', color: Paprika.textPrimary },
  unit: { fontFamily: 'Inter-Regular', fontSize: 14, color: Paprika.textSecondary },
});
